"use client";

import { useState } from "react";
import { METRICS_EVENTS } from "@cede/types";
import { getCexLabel, getCexSignUpUrl } from "@cede/utils";
import { DialogHeader } from "../DialogHeader";
import { CedeIcon, ExchangeIcon, IconStack } from "@/components/icons";
import { ComponentUnderlay } from "@/components/shared/ComponentUnderlay";
import { Button } from "@/components/ui/button";
import { useOAuthFlow, useDevice } from "@/hooks";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useApiKeysSupportMap } from "@/hooks/exchangeAuthentication/useApiKeysSupportMap";
import { useCreateAccountStore } from "@/store/accountStore";
import { useConnectStore } from "@/store/connectStore";
import { enqueueSnackbar } from "notistack";

interface LinkStepProps {
  title: string;
  subtitle: string;
  showBackButton: boolean;
  onNext: () => void;
  onBack: () => void;
}

export const LinkStep = ({ title, subtitle, showBackButton, onNext, onBack }: LinkStepProps) => {
  const { formData } = useCreateAccountStore();
  const { startOAuthFlow, isLoading } = useOAuthFlow();
  const { isMobile } = useDevice();
  const { activeStep, flowType } = useConnectStore();
  const { trackEvent } = useMetricsEvents();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const apiKeysSupportMap = useApiKeysSupportMap();

  const handleSignUp = (cex: string) => {
    const url = getCexSignUpUrl(cex);
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const handleOAuthClick = async () => {
    try {
      setIsRedirecting(true);
      // Track when user proceeds to OAuth flow in connect account
      trackEvent(METRICS_EVENTS.CONNECT_ACCOUNT_NEXT, {
        flowType,
        currentStep: activeStep,
        nextStep: "OAuth",
        cex: formData.selectedCex,
      });
      await startOAuthFlow({
        exchangeId: formData.selectedCex,
        successUrl: window.location.href,
      });
    } catch (error) {
      enqueueSnackbar(`Failed to add exchange account: ${error instanceof Error ? error.message : "Unknown error"}`, {
        variant: "error",
      });
      setIsRedirecting(false);
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 w-full">
      {isMobile ? (
        <div className="flex flex-col flex-1 min-h-0 w-full">
          <DialogHeader
            title={`Connect ${getCexLabel(formData.selectedCex)}`}
            showBackButton={showBackButton}
            centerTitle
            onBack={onBack}
          />
          <div className="flex-1 min-h-0 overflow-y-auto scrollbar-visible px-2">
            <div className="flex min-h-full flex-col items-center justify-center gap-[17px] py-6 pb-4">
              <div className="flex justify-center relative">
                <div className="absolute w-[146px] h-[146px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gradient-radial from-[rgba(94,166,255,1)] to-[rgba(56,100,153,1)] blur-[63.2px] z-0" />
                <IconStack overlap="md" className="relative z-10 [&>div]:bg-transparent [&>div]:p-0">
                  <ComponentUnderlay size={56}>
                    <CedeIcon width={40} height={40} />
                  </ComponentUnderlay>
                  <ExchangeIcon
                    exchange={formData.selectedCex}
                    size={32}
                    aria-label={formData.selectedCex}
                    withUnderlay
                    underlaySize={56}
                  />
                </IconStack>
              </div>
              <div className="flex flex-col items-center justify-center gap-[17px] mt-6">
                <h4 className="text-lg font-semibold text-center text-cede-text-primary">{title}</h4>
                <p className="text-sm text-center text-cede-text-secondary">{subtitle}</p>
              </div>
            </div>
          </div>

          <div className="flex-shrink-0 flex flex-col gap-3 px-2 pb-2">
            <Button
              variant="tertiary"
              size="large"
              className="w-full"
              disabled={isRedirecting}
              loading={isLoading || isRedirecting}
              onClick={handleOAuthClick}
            >
              Authenticate via {getCexLabel(formData.selectedCex)}
            </Button>

            {apiKeysSupportMap[formData.selectedCex] && (
              <>
                <div className="flex items-center w-full opacity-70">
                  <div className="flex-1 h-px bg-cede-border-container" />
                  <p className="text-sm px-2 text-cede-text-secondary">OR</p>
                  <div className="flex-1 h-px bg-cede-border-container" />
                </div>

                <Button
                  variant="secondary"
                  size="large"
                  className="w-full"
                  disabled={isLoading}
                  onClick={() => onNext()}
                >
                  Use API keys
                </Button>
              </>
            )}
            <div className="mt-2 text-center z-10">
              <p className="text-sm text-cede-text-secondary">
                Don't have a {getCexLabel(formData.selectedCex)} account?{" "}
                <button
                  type="button"
                  className="border-0 text-cede-primary cursor-pointer hover:underline"
                  onClick={() => handleSignUp(formData.selectedCex)}
                >
                  Sign up here
                </button>
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="w-full max-w-[600px] flex flex-1 min-h-0 flex-col">
          <DialogHeader title={title} showBackButton={showBackButton} onBack={onBack} />

          <div className="flex-1 min-h-0 overflow-y-auto scrollbar-visible">
            <div className="flex flex-col items-center gap-3 mt-10 px-6">
              <div className="flex justify-center items-center relative w-full mb-2">
                <div className="absolute w-[120px] h-[120px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gradient-radial from-[rgba(94,166,255,1)] to-[rgba(56,100,153,1)] blur-[63.2px] z-0" />
                <IconStack overlap="md" className="relative z-10 [&>div]:bg-transparent [&>div]:p-0">
                  <ComponentUnderlay size={56}>
                    <CedeIcon width={40} height={40} />
                  </ComponentUnderlay>
                  <ExchangeIcon
                    exchange={formData.selectedCex}
                    size={40}
                    aria-label={formData.selectedCex}
                    withUnderlay
                    underlaySize={56}
                  />
                </IconStack>
              </div>

              {subtitle && (
                <p className="text-sm text-center max-w-[450px] mb-2 z-10 text-cede-text-secondary">{subtitle}</p>
              )}

              <div className="flex flex-col items-center gap-3 w-full max-w-[400px] z-10">
                <Button
                  variant="tertiary"
                  size="large"
                  className="w-full"
                  loading={isLoading}
                  onClick={handleOAuthClick}
                >
                  Authenticate via {getCexLabel(formData.selectedCex)} (recommended)
                </Button>

                {apiKeysSupportMap[formData.selectedCex] && (
                  <>
                    <div className="flex items-center w-full opacity-70">
                      <div className="flex-1 h-px bg-cede-border-container" />
                      <p className="text-sm px-2 text-cede-text-secondary">OR</p>
                      <div className="flex-1 h-px bg-cede-border-container" />
                    </div>

                    <Button
                      variant="secondary"
                      size="large"
                      className="w-full"
                      disabled={isLoading}
                      onClick={() => onNext()}
                    >
                      Use API keys
                    </Button>
                  </>
                )}
              </div>

              <div className="mt-2 text-center z-10 pb-6">
                <p className="text-sm text-cede-text-secondary">
                  Don't have a {getCexLabel(formData.selectedCex)} account?{" "}
                  <button
                    type="button"
                    className="border-0 text-cede-primary cursor-pointer hover:underline"
                    onClick={() => handleSignUp(formData.selectedCex)}
                  >
                    Sign up here
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
